import { enqueue, loadContext, saveContext } from './db.js';
import type { QueuedAttachment, QueuedTrip } from './db.js';
import { requestBackgroundSync } from './register.js';
import { uuid } from '../uuid.js';

/**
 * Putting work into the offline queue.
 *
 * A thin layer over `db.enqueue`, and it earns its place by owning two things
 * that would otherwise be repeated at every call site and eventually forgotten
 * at one of them:
 *
 *   - the **idempotency key**, generated here and stored with the item, so
 *     every attempt at sending it carries the same one. That is what makes a
 *     retry after a lost response safe rather than a second copy of the
 *     receipt;
 *   - asking the browser for a background sync, where it has one.
 */

/** Tell the queue where to send things. Called whenever the company changes. */
export async function rememberContext(
  apiBaseUrl: string,
  companyId: string,
): Promise<void> {
  await saveContext({ apiBaseUrl, companyId });
}

async function currentCompany(): Promise<string> {
  const context = await loadContext();
  if (context === undefined) {
    // Reachable only if something queues before the app has resolved a
    // company, which the protected-route boundary prevents. Failing loudly is
    // right: a queued item with no company could never be sent.
    throw new Error('No company is selected, so there is nowhere to queue this.');
  }
  return context.companyId;
}

export async function queueAttachment(
  payload: Omit<QueuedAttachment, 'kind'>,
): Promise<void> {
  await enqueue({
    companyId: await currentCompany(),
    idempotencyKey: uuid(),
    payload: { kind: 'attachment', ...payload },
  });
  await requestBackgroundSync();
}

export async function queueTrip(payload: Omit<QueuedTrip, 'kind'>): Promise<void> {
  await enqueue({
    companyId: await currentCompany(),
    idempotencyKey: uuid(),
    payload: { kind: 'trip', ...payload },
  });
  await requestBackgroundSync();
}
